import React, { useState, useEffect, useCallback, useRef } from 'react';

// 제외극장 관리 (2026-08-08 신설)
// 관 이름이 평범한데(01관) 실제 예매 불가인 극장(전관 특별관, 예: 메가박스 청주터미널)을 예매목록에서 숨김.
// 극장 검색 자동완성으로 추가 → crm_movie.excluded_theaters 저장 → 영화거래소·씨네트리 예매화면에서 즉시 제외.
// 하단: 극장별 대행 취소사유(특별관) 집계 — 제외할 극장 찾는 참고 자료.
// 상대경로 fetch → domainFetch가 X-Domain 자동 부착.

const API = '/request';

export default function ExcludedTheaters() {
  const [list, setList] = useState([]);       // 제외극장 목록
  const [stats, setStats] = useState([]);     // 극장별 취소 집계
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // 검색 자동완성
  const [query, setQuery] = useState('');
  const [suggests, setSuggests] = useState([]);
  const [picked, setPicked] = useState(null); // {theater, region, sub_region, cinema_brand}
  const [reason, setReason] = useState('');
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/getExcludedTheaters`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/getAgentCancelStats`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setList(r1.ok ? r1.theaters : []);
      setStats(r2.ok ? r2.stats : []);
    } catch (e) { setErr('목록을 불러오지 못했습니다.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 자동완성 (300ms 디바운스)
  const onQueryChange = (v) => {
    setQuery(v); setPicked(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v.trim()) { setSuggests([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const d = await (await fetch(`${API}/getScheduleTheaters?q=${encodeURIComponent(v.trim())}`, { credentials: 'include' })).json();
        setSuggests(d.ok ? d.theaters : []);
      } catch (e) { setSuggests([]); }
    }, 300);
  };

  const pick = (t) => { setPicked(t); setQuery(t.theater); setSuggests([]); };

  const post = async (path, body) => {
    const r = await fetch(`${API}/${path}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    let d = {}; try { d = await r.json(); } catch (e) {}
    if (!r.ok || !d.ok) throw new Error(d.error || '요청 실패');
    return d;
  };

  const onAdd = async () => {
    const theater = (picked?.theater || query).trim();
    if (!theater) { alert('극장을 선택하거나 입력하세요.'); return; }
    try {
      await post('addExcludedTheater', {
        theater,
        region: picked?.region || null,
        sub_region: picked?.sub_region || null,
        cinema_brand: picked?.cinema_brand || null,
        reason: reason.trim() || null,
      });
      alert(`'${theater}' 를 제외극장에 추가했습니다. 예매목록에서 바로 사라집니다.`);
      setQuery(''); setPicked(null); setReason(''); setSuggests([]);
      load();
    } catch (e) { alert('추가 실패: ' + e.message); }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`'${row.theater}' 를 제외 해제하시겠습니까? 다시 예매목록에 나타납니다.`)) return;
    try { await post('deleteExcludedTheater', { id: row.id }); load(); }
    catch (e) { alert('삭제 실패: ' + e.message); }
  };

  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#555', borderBottom: '2px solid #e5e5e5', whiteSpace: 'nowrap' };
  const td = { padding: '7px 10px', borderBottom: '1px solid #eee', fontSize: 13 };
  const inp = { padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 13, boxSizing: 'border-box' };
  const btn = (bg) => ({ padding: '5px 12px', borderRadius: 5, border: 0, background: bg, color: '#fff', fontSize: 12, cursor: 'pointer', margin: '0 2px' });

  return (
    <div style={{ padding: 24, width: '100%', maxWidth: 1100 }}>
      <h2 style={{ margin: '0 0 4px' }}>제외극장 관리</h2>
      <p style={{ color: '#777', fontSize: 13, margin: '0 0 18px' }}>
        관 이름은 평범한데 실제로 예매가 안 되는 극장(전관 특별관 등)을 예매목록에서 숨깁니다.
        여기 추가하면 <b>영화거래소·씨네트리 둘 다</b> 그 극장이 안 나옵니다. (데이터는 유지, 화면에서만 숨김)
      </p>

      {/* 추가 폼 */}
      <div style={{ border: '1px solid #cfe0f5', background: '#f3f8ff', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 10, color: '#1565c0' }}>극장 추가</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ position: 'relative', flex: '1 1 320px' }}>
            <input style={{ ...inp, width: '100%' }} placeholder="극장명 검색 (예: 청주터미널)"
              value={query} onChange={e => onQueryChange(e.target.value)} autoComplete="off" />
            {suggests.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ccc', borderRadius: 6, maxHeight: 240, overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                {suggests.map((t, i) => (
                  <div key={i} onClick={() => pick(t)}
                    style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0' }}
                    onMouseDown={e => e.preventDefault()}>
                    <b>{t.theater}</b>
                    <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>{t.region} {t.sub_region}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <input style={{ ...inp, flex: '1 1 220px' }} placeholder="사유(선택, 예: 전관 특별관)"
            value={reason} onChange={e => setReason(e.target.value)} />
          <button onClick={onAdd} style={{ ...btn('#1976d2'), padding: '9px 18px' }}>제외 추가</button>
        </div>
        {picked && <div style={{ marginTop: 8, fontSize: 12, color: '#1565c0' }}>선택됨: {picked.theater} ({picked.region} {picked.sub_region} / {picked.cinema_brand})</div>}
      </div>

      {err && <div style={{ color: '#c62828', marginBottom: 10, fontSize: 13 }}>{err}</div>}

      {/* 제외극장 목록 */}
      <div style={{ border: '1px solid #e5e5e5', borderRadius: 10, marginBottom: 26, overflow: 'hidden' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#555', padding: '12px 14px', background: '#fafafa', borderBottom: '1px solid #eee' }}>
          제외된 극장 ({list.length})
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>극장</th><th style={th}>지역</th><th style={th}>브랜드</th>
              <th style={th}>사유</th><th style={th}>등록자</th><th style={th}>등록일</th>
              <th style={{ ...th, textAlign: 'center' }}>관리</th>
            </tr></thead>
            <tbody>
              {list.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, fontWeight: 700 }}>{r.theater}</td>
                  <td style={td}>{[r.region, r.sub_region].filter(Boolean).join(' ') || '-'}</td>
                  <td style={td}>{r.cinema_brand || '-'}</td>
                  <td style={td}>{r.reason || '-'}</td>
                  <td style={{ ...td, color: '#888' }}>{r.created_by || '-'}</td>
                  <td style={{ ...td, color: '#999', fontSize: 12 }}>{r.created_at || '-'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button onClick={() => onDelete(r)} style={btn('#dc2626')}>제외 해제</button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#999' }}>{loading ? '불러오는 중…' : '제외된 극장이 없습니다'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* 극장별 대행 취소 참고표 */}
      <div style={{ border: '1px solid #e5e5e5', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#555', padding: '12px 14px', background: '#fafafa', borderBottom: '1px solid #eee' }}>
          극장별 대행 취소 참고 (특별관 사유로 취소가 잦은 극장 = 제외 후보)
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>극장</th>
              <th style={{ ...th, textAlign: 'right' }}>특별관 취소</th>
              <th style={{ ...th, textAlign: 'right' }}>전체 취소</th>
              <th style={th}>마지막 특별관취소</th>
              <th style={th}>마지막 취소</th>
            </tr></thead>
            <tbody>
              {stats.map((s, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 700 }}>{s.theater}</td>
                  <td style={{ ...td, textAlign: 'right', color: Number(s.special_cancel) > 0 ? '#dc2626' : '#999', fontWeight: 700 }}>{s.special_cancel}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{s.total_cancel}</td>
                  <td style={{ ...td, color: '#888', fontSize: 12 }}>{s.last_special_cancel || '-'}</td>
                  <td style={{ ...td, color: '#999', fontSize: 12 }}>{s.last_cancel || '-'}</td>
                </tr>
              ))}
              {stats.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#999' }}>아직 취소 기록이 없습니다. (지금부터 대행 취소가 여기 쌓입니다)</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: '#999', padding: '8px 14px' }}>
          * 대행자가 취소할 때 사유·극장이 기록됩니다. "특별관 취소"가 잦은 극장을 위에서 제외극장으로 추가하세요.
        </div>
      </div>
    </div>
  );
}
